import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PopUpWrapper, PopUpContent, PopupOuter} from './styles';
import {BTag} from 'core/components';
import { Tag } from '@brg/api';
import {useTranslation} from 'react-i18next';
import {bTag} from 'core';
import {CreateLinkTo} from 'shared/constants/linkTo';
import {BTagsGridNew} from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import {Link} from "../link";
import {AppTester} from "../../../supporting";
import {ThemeProvider} from "styled-components";
import {ApplicationTheme} from "../../themes";
import { useTags } from 'shared/hooks/api/useTagsList';

export type TagsCoor = {
    x: number | null,
    y: number | null
}

const FULL_SIZE_POPUP_CONDITION = 3;

export const TagsPopUp: FC<{
    list: any,
    currentTab: string | null;
    visible?: boolean;
    coor?: TagsCoor;
    blueSelect?: boolean;
    onTagClick?: (tagId: string) => void;
    onMouseEnter?: () => void;
    tagIdsOverride?: string[] | null;
    anchorBelowParent?: boolean;
    tagLinkTarget?: '_self' | '_blank';
}> = ({
          list,
          currentTab,
          visible,
          coor,
          blueSelect,
          onTagClick,
          onMouseEnter,
          tagIdsOverride,
          anchorBelowParent,
          tagLinkTarget = '_blank',
      }) => {
    const {i18n} = useTranslation();
    const prevRef = useRef<any>(null);
    const [tags, setTags] = useState<Tag[]>([]);
    const [tagsForTab, setTagsForTab] = useState<string | null>(null);

    const hasCoor = useMemo(() => (coor?.x !== null && coor?.y !== null), [coor])

    const tabListValue = useMemo(() => {
        if (!list?.length || !hasCoor || !currentTab || tagIdsOverride?.length) {
            return undefined;
        }

        if (currentTab === 'all') {
            return list.reduce((acc: unknown[], item: any) => {
                Object.keys(item).forEach((key) => {
                    if (key !== 'all' && Array.isArray(item[key])) {
                        acc.push(...item[key]);
                    }
                });
                return acc;
            }, []);
        }

        return list.filter((i: any) => {
            if (!i[currentTab]?.includes?.('geo_all')) {
                return i[currentTab];
            }
        })[0]?.[currentTab];
    }, [list, currentTab, hasCoor, tagIdsOverride]);

    const preloadedTags = useMemo((): Tag[] | undefined => {
        if (!Array.isArray(tabListValue) || !tabListValue.length) {
            return undefined;
        }
        if (tabListValue[0]?.tagId) {
            return tabListValue as Tag[];
        }
        return undefined;
    }, [tabListValue]);

    const idsList = useMemo((): string[] | undefined => {
        if (preloadedTags?.length) {
            return undefined;
        }
        if (tagIdsOverride?.length && currentTab) {
            return tagIdsOverride;
        }
        if (Array.isArray(tabListValue) && tabListValue.length && typeof tabListValue[0] === 'string') {
            return tabListValue as string[];
        }
        return undefined;
    }, [preloadedTags, tagIdsOverride, currentTab, tabListValue]);

    const tagsQueryToken = idsList?.join(',') ?? '';
    const [tagsData] = useTags(
        { tagIds: idsList ?? [] },
        i18n.language,
        tagsQueryToken,
        !idsList?.length,
        true,
    );

    const getTagSortPriority = useCallback((tag: any): number => {
        const {
            isTagCategory,
            isEducationTag,
            isTeamTag,
            level,
            hasEduTags,
            hasCommonTags,
        } = tag;

        if (isTagCategory && level === 2 && hasEduTags && !isTeamTag) {
            return 1;
        }
        if (isEducationTag && !isTagCategory && level === 0 && !isTeamTag) {
            return 2;
        }
        if (!isTeamTag && level === 2 && hasCommonTags && !isEducationTag) {
            return 3;
        }
        if (!isEducationTag && !isTagCategory && level === 0 && !isTeamTag) {
            return 4;
        }

        if (isTeamTag) {
            return 5;
        }

        return 5;
    }, []);

    useEffect(() => {
        const sourceTags = preloadedTags?.length ? preloadedTags : tagsData;
        if (sourceTags?.length && currentTab) {
            setTags([...sourceTags].sort((a, b) => {
                const priorityA = getTagSortPriority(a);
                const priorityB = getTagSortPriority(b);

                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }

                return a.isEducationTag ? -1 : b.isEducationTag ? 1 : 0;
            }));
            setTagsForTab(currentTab);
        }
    }, [preloadedTags, tagsData, currentTab, getTagSortPriority]);

    const showPopUp = useMemo(() => {
        const hasTagSource = !!(idsList?.length || preloadedTags?.length);
        if (!visible || !hasTagSource) {
            return false;
        }
        if (tagIdsOverride?.length) {
            return true;
        }
        return !!list?.length;
    }, [visible, idsList, preloadedTags, list, tagIdsOverride]);

    const fs = useMemo(() => (!!tags?.length && tags?.length >= FULL_SIZE_POPUP_CONDITION), [tags]);

    const clear = useCallback(() => {
        setTags([]);
        setTagsForTab(null);
    }, []);

    useEffect(() => {
        if (currentTab !== prevRef.current) {
            if (currentTab === null) {
                clear()
            }
        }
        prevRef.current = currentTab
    }, [currentTab, clear])

    const isBooksGallllery = AppTester.isBooksGallllery();

    const shouldRender = currentTab !== 'all' && showPopUp;
    const isVisible = showPopUp && tags?.length > 0 && tagsForTab === currentTab;
    
    const getCategoryColorTheme = (value: any) => {
        if (value?.level !== 2) return undefined;
        const bg = value?.hasEduTags
            ? '#102D69'
            : 'rgba(16, 45, 105, 0.50)';
        return {
            fill: { default: bg, hover: bg, disabled: bg },
            text: { default: '#FFF', hover: '#FFF', disabled: '#FFF' },
        };
    };

    const popupContent = !shouldRender ? null : (
        <PopUpWrapper
            key={currentTab}
            className={'tags-popup-wrapper'}
            positionY={coor?.y}
            positionX={coor?.x}
            visible={isVisible}
            fs={fs}
            blueSelect={blueSelect}
            onMouseEnter={onMouseEnter}
            anchorBelowParent={anchorBelowParent}
        >
            {tags?.length > 0 && (
                <PopupOuter $anchorBelowParent={anchorBelowParent}>
                    <PopUpContent>
                        <BTagsGridNew>
                            {tags && tags.map((value: any) => (
                                <Link 
                                    key={value.tagId}
                                    to={CreateLinkTo.tag(value?.tagId)} 
                                    title={bTag.title(value)} 
                                    type="tag"
                                    target={tagLinkTarget}
                                >
                                    <BTag
                                        tag={value}
                                        educational={value.isEducationTag}
                                        organization={value.isTeamTag}
                                        isInfoTags
                                        colorTheme={getCategoryColorTheme(value)}
                                    >
                                        {bTag.title(value)}
                                    </BTag>
                                </Link>
                            ))}
                        </BTagsGridNew>
                    </PopUpContent>
                </PopupOuter>
            )}
        </PopUpWrapper>
    );

    if (isBooksGallllery) {
        return (
            <ThemeProvider theme={ApplicationTheme.white}>
                {popupContent}
            </ThemeProvider>
        );
    }

    return popupContent;
}
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PopUpWrapper, PopUpContent, PopupOuter} from './styles';
import {BTag} from 'core/components';
import { Tag } from '@brg/api';
import {useTranslation} from 'react-i18next';
import {bTag} from 'core';
import {CreateLinkTo} from 'shared/constants/linkTo';
import {BTagsGridNew} from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import {Link} from "../link";
import {AppTester} from "../../../supporting";
import {ThemeProvider} from "styled-components";
import {ApplicationTheme} from "../../themes";
import { useTags } from 'shared/hooks/api/useTagsList';

export type TagsCoor = {
    x: number | null,
    y: number | null
}

const FULL_SIZE_POPUP_CONDITION = 3;

export const TagsPopUp: FC<{
    list: any,
    currentTab: string | null;
    visible?: boolean;
    coor?: TagsCoor;
    blueSelect?: boolean;
    onTagClick?: (tagId: string) => void;
    onMouseEnter?: () => void;
    tagIdsOverride?: string[] | null;
    anchorBelowParent?: boolean;
    tagLinkTarget?: '_self' | '_blank';
}> = ({
          list,
          currentTab,
          visible,
          coor,
          blueSelect,
          onTagClick,
          onMouseEnter,
          tagIdsOverride,
          anchorBelowParent,
          tagLinkTarget = '_blank',
      }) => {
    const {i18n} = useTranslation();
    const prevRef = useRef<any>(null);
    const [tags, setTags] = useState<Tag[]>([]);
    const [tagsForTab, setTagsForTab] = useState<string | null>(null);

    const hasCoor = useMemo(() => (coor?.x !== null && coor?.y !== null), [coor])

    const tabListValue = useMemo(() => {
        if (!list?.length || !hasCoor || !currentTab || tagIdsOverride?.length) {
            return undefined;
        }

        if (currentTab === 'all') {
            return list.reduce((acc: unknown[], item: any) => {
                Object.keys(item).forEach((key) => {
                    if (key !== 'all' && Array.isArray(item[key])) {
                        acc.push(...item[key]);
                    }
                });
                return acc;
            }, []);
        }

        return list.filter((i: any) => {
            if (!i[currentTab]?.includes?.('geo_all')) {
                return i[currentTab];
            }
        })[0]?.[currentTab];
    }, [list, currentTab, hasCoor, tagIdsOverride]);

    const preloadedTags = useMemo((): Tag[] | undefined => {
        if (!Array.isArray(tabListValue) || !tabListValue.length) {
            return undefined;
        }
        if (tabListValue[0]?.tagId) {
            return tabListValue as Tag[];
        }
        return undefined;
    }, [tabListValue]);

    const idsList = useMemo((): string[] | undefined => {
        if (preloadedTags?.length) {
            return undefined;
        }
        if (tagIdsOverride?.length && currentTab) {
            return tagIdsOverride;
        }
        if (Array.isArray(tabListValue) && tabListValue.length && typeof tabListValue[0] === 'string') {
            return tabListValue as string[];
        }
        return undefined;
    }, [preloadedTags, tagIdsOverride, currentTab, tabListValue]);

    const tagsQueryToken = idsList?.join(',') ?? '';
    const [tagsData] = useTags(
        { tagIds: idsList ?? [] },
        i18n.language,
        tagsQueryToken,
        !idsList?.length,
        true,
    );

    const getTagSortPriority = useCallback((tag: any): number => {
        const {
            isTagCategory,
            isEducationTag,
            isTeamTag,
            level,
            hasEduTags,
            hasCommonTags,
        } = tag;

        if (isTagCategory && level === 2 && hasEduTags && !isTeamTag) {
            return 1;
        }
        if (isEducationTag && !isTagCategory && level === 0 && !isTeamTag) {
            return 2;
        }
        if (!isTeamTag && level === 2 && hasCommonTags && !isEducationTag) {
            return 3;
        }
        if (!isEducationTag && !isTagCategory && level === 0 && !isTeamTag) {
            return 4;
        }

        if (isTeamTag) {
            return 5;
        }

        return 5;
    }, []);

    useEffect(() => {
        const sourceTags = preloadedTags?.length ? preloadedTags : tagsData;
        if (sourceTags?.length && currentTab) {
            setTags([...sourceTags].sort((a, b) => {
                const priorityA = getTagSortPriority(a);
                const priorityB = getTagSortPriority(b);

                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }

                return a.isEducationTag ? -1 : b.isEducationTag ? 1 : 0;
            }));
            setTagsForTab(currentTab);
        }
    }, [preloadedTags, tagsData, currentTab, getTagSortPriority]);

    const showPopUp = useMemo(() => {
        const hasTagSource = !!(idsList?.length || preloadedTags?.length);
        if (!visible || !hasTagSource) {
            return false;
        }
        if (tagIdsOverride?.length) {
            return true;
        }
        return !!list?.length;
    }, [visible, idsList, preloadedTags, list, tagIdsOverride]);

    const fs = useMemo(() => (!!tags?.length && tags?.length >= FULL_SIZE_POPUP_CONDITION), [tags]);

    const clear = useCallback(() => {
        setTags([]);
        setTagsForTab(null);
    }, []);

    useEffect(() => {
        if (currentTab !== prevRef.current) {
            if (currentTab === null) {
                clear()
            }
        }
        prevRef.current = currentTab
    }, [currentTab, clear])

    const isBooksGallllery = AppTester.isBooksGallllery();

    const shouldRender = currentTab !== 'all' && showPopUp;
    const isVisible = showPopUp && tags?.length > 0 && tagsForTab === currentTab;
    
    const getCategoryColorTheme = (value: any) => {
        if (value?.level !== 2) return undefined;
        const bg = value?.hasEduTags
            ? '#102D69'
            : 'rgba(16, 45, 105, 0.50)';
        return {
            fill: { default: bg, hover: bg, disabled: bg },
            text: { default: '#FFF', hover: '#FFF', disabled: '#FFF' },
        };
    };

    const popupContent = !shouldRender ? null : (
        <PopUpWrapper
            key={currentTab}
            className={'tags-popup-wrapper'}
            positionY={coor?.y}
            positionX={coor?.x}
            visible={isVisible}
            fs={fs}
            blueSelect={blueSelect}
            onMouseEnter={onMouseEnter}
            anchorBelowParent={anchorBelowParent}
        >
            {tags?.length > 0 && (
                <PopupOuter $anchorBelowParent={anchorBelowParent}>
                    <PopUpContent>
                        <BTagsGridNew>
                            {tags && tags.map((value: any) => (
                                <Link 
                                    key={value.tagId}
                                    to={CreateLinkTo.tag(value?.tagId)} 
                                    title={bTag.title(value)} 
                                    type="tag"
                                    target={tagLinkTarget}
                                >
                                    <BTag
                                        tag={value}
                                        educational={value.isEducationTag}
                                        organization={value.isTeamTag}
                                        isInfoTags
                                        colorTheme={getCategoryColorTheme(value)}
                                    >
                                        {bTag.title(value)}
                                    </BTag>
                                </Link>
                            ))}
                        </BTagsGridNew>
                    </PopUpContent>
                </PopupOuter>
            )}
        </PopUpWrapper>
    );

    if (isBooksGallllery) {
        return (
            <ThemeProvider theme={ApplicationTheme.white}>
                {popupContent}
            </ThemeProvider>
        );
    }

    return popupContent;
}
